import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import MyKuraTab from './MyKuraTab';
import RequestHistory from './RequestHistory';

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  zIndex: 2000,
  backdropFilter: 'blur(4px)',
};

const modalStyle = {
  background: '#1a1a1a',
  borderRadius: '20px 20px 0 0',
  width: '100%',
  maxHeight: '85vh',
  padding: '20px',
  paddingBottom: '32px',
  animation: 'slideUp 0.3s ease-out',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const handleBarStyle = {
  width: 40,
  height: 4,
  background: '#444',
  borderRadius: 2,
  margin: '0 auto 20px',
};

const userInfoStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 0',
  borderBottom: '1px solid #333',
  marginBottom: 16,
};

const avatarStyle = {
  width: 44,
  height: 44,
  borderRadius: '50%',
  overflow: 'hidden',
  background: '#333',
};

const userDetailsStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  flex: 1,
  overflow: 'hidden',
};

const userNameStyle = {
  color: '#fff',
  fontSize: 16,
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const userEmailStyle = {
  color: '#888',
  fontSize: 13,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const menuItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 12px',
  color: '#ccc',
  fontSize: 15,
  fontWeight: 500,
  cursor: 'pointer',
  borderRadius: 8,
  transition: 'all 0.2s',
  marginBottom: 4,
};

const menuIconStyle = {
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const logoutStyle = {
  ...menuItemStyle,
  color: '#ff4d4d',
  marginTop: 8,
  borderTop: '1px solid #333',
  paddingTop: 18,
};

const UserSettingsModalMobile = ({ user, onClose }) => {
  const location = useLocation();
  const previousCollectionIdRef = useRef(null);
  const [activeTab, setActiveTab] = useState('myKura');

  // Auto close modal only when switching to a different collection (not when initially opening on collection page)
  useEffect(() => {
    const match = location.pathname.match(/\/collection\/([^/]+)/);
    if (match) {
      const currentCollectionId = match[1];
      // Only close if we're switching from one collection to another
      if (previousCollectionIdRef.current !== null && previousCollectionIdRef.current !== currentCollectionId) {
        onClose();
      }
      previousCollectionIdRef.current = currentCollectionId;
    } else {
      // Reset when not on a collection page
      previousCollectionIdRef.current = null;
    }
  }, [location.pathname, onClose]);
  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/';
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const tabContentStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 0',
    minHeight: 0,
  };

  return (
    <>
      <style>
        {`
          @keyframes slideUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>
      <div style={overlayStyle} onClick={handleOverlayClick}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {/* Handle bar for swipe indication */}
          <div style={handleBarStyle} />

          {/* User Info */}
          <div style={userInfoStyle}>
            <div style={avatarStyle}>
              <img
                src={user?.user_metadata?.avatar_url || '/user-profile.png'}
                alt="Avatar"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={userDetailsStyle}>
              <div style={userNameStyle}>
                {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}
              </div>
              <div style={userEmailStyle}>
                {user?.email || ''}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div 
            style={{
              ...menuItemStyle,
              background: activeTab === 'myKura' ? 'rgba(130, 205, 89, 0.1)' : 'transparent',
              color: activeTab === 'myKura' ? '#82CD59' : '#ccc',
            }}
            onClick={() => setActiveTab('myKura')}
          >
            <div style={menuIconStyle}>
              <img src="/box-icon.png" alt="My Kura" style={{ width: 20, height: 20, filter: activeTab === 'myKura' ? 'brightness(0) invert(0)' : 'brightness(0) invert(0.8)' }} />
            </div>
            <span>My Kura</span>
          </div>

          <div 
            style={{
              ...menuItemStyle,
              background: activeTab === 'history' ? 'rgba(130, 205, 89, 0.1)' : 'transparent',
              color: activeTab === 'history' ? '#82CD59' : '#ccc',
            }}
            onClick={() => setActiveTab('history')}
          >
            <div style={menuIconStyle}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: activeTab === 'history' ? '#000' : '#ccc' }}>
                <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 6V10L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span>History</span>
          </div>

          <div 
            style={{
              ...menuItemStyle,
              background: activeTab === 'settings' ? 'rgba(130, 205, 89, 0.1)' : 'transparent',
              color: activeTab === 'settings' ? '#82CD59' : '#ccc',
            }}
            onClick={() => setActiveTab('settings')}
          >
            <div style={menuIconStyle}>
              <img src="/setting-icon.png" alt="Settings" style={{ width: 20, height: 20, filter: activeTab === 'settings' ? 'brightness(0) invert(0)' : 'brightness(0) invert(0.8)' }} />
            </div>
            <span>Settings</span>
          </div>

          <div 
            style={{
              ...menuItemStyle,
              background: activeTab === 'account' ? 'rgba(130, 205, 89, 0.1)' : 'transparent',
              color: activeTab === 'account' ? '#82CD59' : '#ccc',
            }}
            onClick={() => setActiveTab('account')}
          >
            <div style={menuIconStyle}>
              <img src="/profile-icon.png" alt="Account" style={{ width: 20, height: 20, filter: activeTab === 'account' ? 'brightness(0) invert(0)' : 'brightness(0) invert(0.8)' }} />
            </div>
            <span>Account</span>
          </div>

          <div 
            style={{
              ...menuItemStyle,
              background: activeTab === 'contact' ? 'rgba(130, 205, 89, 0.1)' : 'transparent',
              color: activeTab === 'contact' ? '#82CD59' : '#ccc',
            }}
            onClick={() => setActiveTab('contact')}
          >
            <div style={menuIconStyle}>
              <img src="/letter-icon.png" alt="Contact" style={{ width: 20, height: 20, filter: activeTab === 'contact' ? 'brightness(0) invert(0)' : 'brightness(0) invert(0.8)' }} />
            </div>
            <span>Contact us</span>
          </div>

          {/* Tab Content Area */}
          <div style={tabContentStyle}>
            {activeTab === 'myKura' && (
              <div style={{ color: '#fff' }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 16px 0', color: '#fff' }}>
                  {`${(user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User') + '\'s Kura'}`}
                </h2>
                <MyKuraTab />
              </div>
            )}
            {activeTab === 'history' && (
              <div style={{ color: '#fff', height: '100%' }}>
                <RequestHistory onRequestSelect={onClose} />
              </div>
            )}
            {activeTab === 'settings' && (
              <div style={{ color: '#fff' }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 16px 0', color: '#fff' }}>Settings</h2>
              </div>
            )}
            {activeTab === 'account' && (
              <div style={{ color: '#fff' }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 16px 0', color: '#fff' }}>Account</h2>
              </div>
            )}
            {activeTab === 'contact' && (
              <div style={{ color: '#fff' }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 16px 0', color: '#fff' }}>Contact us</h2>
              </div>
            )}
          </div>

          {/* Logout */}
          <div style={logoutStyle} onClick={handleLogout}>
            <span>Log out</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserSettingsModalMobile;

